// Floating ambient particles scattered across the map: warm dust motes /
// fireflies year-round (strongest in spring/summer), plus a gravity system
// that is autumn leaves and turns into winter snowfall — the mix comes from
// seasonParticleMix(day).
import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { RenderLayer } from './RenderLayer.js';
import { seasonParticleMix } from './seasonParticles.js';

interface AmbientParticlesProps {
  /** Number of motes. */
  count?: number;
  /** World-space bounds [minX, maxX, minZ, maxZ] or null to auto-fit. */
  bounds?: [number, number, number, number] | null;
  /** Mote size. */
  size?: number;
  /** Mote base color. */
  color?: string;
  /** Current sim day — enables the seasonal leaves/snow system. */
  day?: number;
}

/** Default bounds — kept in sync with the mote/fall loops below. */
const DEFAULT_BOUNDS: [number, number, number, number] = [-20, 20, -15, 15];

export function AmbientParticles({
  count = 200,
  bounds,
  size = 0.08,
  color = '#ffcc88',
  day,
}: AmbientParticlesProps) {
  const pointsRef = useRef<THREE.Points>(null);

  const [positions, velocities] = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const velocities = new Float32Array(count * 3);

    const minX = bounds ? bounds[0] : -20;
    const maxX = bounds ? bounds[1] : 20;
    const minZ = bounds ? bounds[2] : -15;
    const maxZ = bounds ? bounds[3] : 15;

    for (let i = 0; i < count; i++) {
      positions[i * 3] = minX + Math.random() * (maxX - minX);
      positions[i * 3 + 1] = 0.3 + Math.random() * 2.5;
      positions[i * 3 + 2] = minZ + Math.random() * (maxZ - minZ);

      velocities[i * 3] = (Math.random() - 0.5) * 0.005;
      velocities[i * 3 + 1] = (Math.random() - 0.5) * 0.003;
      velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.005;
    }

    return [positions, velocities];
  }, [count, bounds]);

  const spriteTex = useMemo(() => {
    const size = 16;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    gradient.addColorStop(0, 'rgba(255,255,200,0.9)');
    gradient.addColorStop(0.3, 'rgba(255,220,150,0.5)');
    gradient.addColorStop(0.7, 'rgba(255,180,80,0.1)');
    gradient.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
    return new THREE.CanvasTexture(canvas);
  }, []);

  const mix = day !== undefined ? seasonParticleMix(day) : null;

  useFrame(() => {
    const pts = pointsRef.current;
    if (!pts) return;

    const attr = pts.geometry.attributes.position;
    if (!attr) return;
    const pos = attr.array as Float32Array;
    const minX = bounds ? bounds[0] : -20;
    const maxX = bounds ? bounds[1] : 20;
    const minZ = bounds ? bounds[2] : -15;
    const maxZ = bounds ? bounds[3] : 15;

    for (let i = 0; i < count; i++) {
      const idx = i * 3;
      let px = pos[idx]!;
      let py = pos[idx + 1]!;
      let pz = pos[idx + 2]!;
      const vx = velocities[idx]!;
      const vy = velocities[idx + 1]!;
      const vz = velocities[idx + 2]!;

      px += vx;
      py += vy;
      pz += vz;

      // Wrap around bounds.
      if (px > maxX) px = minX;
      if (px < minX) px = maxX;
      if (py > 2.8) py = 0.3;
      if (py < 0.3) py = 2.8;
      if (pz > maxZ) pz = minZ;
      if (pz < minZ) pz = maxZ;

      pos[idx] = px;
      pos[idx + 1] = py;
      pos[idx + 2] = pz;

      // Gentle random walk perturbation.
      const dvx = vx + (Math.random() - 0.5) * 0.0005;
      const dvy = vy + (Math.random() - 0.5) * 0.0003;
      const dvz = vz + (Math.random() - 0.5) * 0.0005;

      // Clamp velocity.
      const maxV = 0.008;
      velocities[idx] = Math.max(-maxV, Math.min(maxV, dvx));
      velocities[idx + 1] = Math.max(-maxV, Math.min(maxV, dvy));
      velocities[idx + 2] = Math.max(-maxV, Math.min(maxV, dvz));
    }

    attr.needsUpdate = true;
  });

  return (
    <>
      <points ref={pointsRef} frustumCulled renderOrder={RenderLayer.Effects}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        </bufferGeometry>
        <pointsMaterial
          map={spriteTex}
          color={color}
          size={size}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          transparent
          opacity={mix ? mix.motesOpacity : 0.6}
        />
      </points>
      {day !== undefined && bounds && <FallParticles bounds={bounds} day={day} />}
    </>
  );
}

/** Soft neutral sprite for the gravity particles (colour comes from the mix). */
function makeFallTexture(): THREE.CanvasTexture {
  const size = 16;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  gradient.addColorStop(0, 'rgba(255,255,255,0.95)');
  gradient.addColorStop(0.5, 'rgba(255,255,255,0.45)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  return new THREE.CanvasTexture(canvas);
}

/**
 * Gravity particles that ride the season mix: autumn leaves (larger, warm,
 * fast, swaying) easing into winter snow (small, white, slow). Hidden outside
 * autumn/winter, blended across the transition window.
 */
function FallParticles({
  bounds,
  day,
}: {
  bounds: [number, number, number, number];
  day: number;
}) {
  const pointsRef = useRef<THREE.Points>(null);
  const count = 150;
  const mix = useMemo(() => seasonParticleMix(day), [day]);

  const [positions, fallRate, phase] = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const fallRate = new Float32Array(count);
    const phase = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      positions[i * 3] = bounds[0] + Math.random() * (bounds[1] - bounds[0]);
      positions[i * 3 + 1] = 0.3 + Math.random() * 2.5;
      positions[i * 3 + 2] = bounds[2] + Math.random() * (bounds[3] - bounds[2]);
      fallRate[i] = 0.7 + Math.random() * 0.6; // per-flake speed variance
      phase[i] = Math.random() * Math.PI * 2;
    }
    return [positions, fallRate, phase];
  }, [bounds]);

  const spriteTex = useMemo(() => makeFallTexture(), []);

  useFrame(({ clock }) => {
    const pts = pointsRef.current;
    if (!pts) return;
    // Outside autumn/winter the mix is fully transparent — skip the sim.
    const visible = mix.fall.opacity >= 0.01;
    pts.visible = visible;
    if (!visible) return;

    const attr = pts.geometry.attributes.position;
    if (!attr) return;
    const pos = attr.array as Float32Array;
    const t = clock.elapsedTime;
    for (let i = 0; i < count; i++) {
      const idx = i * 3;
      let px = pos[idx]!;
      let py = pos[idx + 1]!;
      const pz = pos[idx + 2]!;

      py -= mix.fall.fallSpeed * fallRate[i]!;
      px += Math.sin(t * 1.7 + phase[i]!) * mix.fall.sway;

      // Wrap: respawn at the ceiling, roll around the map bounds.
      if (py < 0.3) py = 2.8;
      if (px > bounds[1]) px = bounds[0];
      if (px < bounds[0]) px = bounds[1];

      pos[idx] = px;
      pos[idx + 1] = py;
      pos[idx + 2] = pz;
    }
    attr.needsUpdate = true;
  });

  const [r, g, b] = mix.fall.color;
  const cssColor = `rgb(${Math.round(r * 255)},${Math.round(g * 255)},${Math.round(b * 255)})`;

  return (
    <points ref={pointsRef} frustumCulled renderOrder={RenderLayer.Effects} visible={false}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        map={spriteTex}
        color={cssColor}
        size={mix.fall.size}
        blending={THREE.NormalBlending}
        depthWrite={false}
        transparent
        opacity={mix.fall.opacity}
      />
    </points>
  );
}
